import React from "react";
import { connect } from "react-redux";
import { Col, Row, Form } from "react-bootstrap";
import PropTypes from "prop-types";

import Table from "../../layout/table";
import { search as getPeople, importPreview, importApply } from "../../actions/people";
import { search as getCompanies } from "../../actions/companies";
import PersonImportModal from "./modals/import";

import ConditionalButton from "../../layout/conditional-button";
import { send as pageInfo, loading } from "../../actions/page";
import { withNavigation } from "../../utils/wrapper";
import { initialState, onLinkFormat, onPhoneLinkFormat, onColumnClick } from "../../layout/table/helpers";

class People extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            ...initialState({ size: 10, order: "lastName"}),
            archived: 0,
            converted: 0,
            import: {
                show: false,
                file: null,
                preview: null,
                resolutions: {},
                importing: false,
            },
            filter: {
                lead: true,
                client: true,
                attorney: false,
                judge: false,
                staff: false,
                vendor: false
            }
        };
        this.props.loading(true);
    }

    componentDidMount = () => {
        setTimeout(async () => {
            await this.loadData();
        }, 30);
    }

    calculateFlags = () => {
        if (this.props.company) return 0;
        let result = 0;
        result += this.state.filter.lead ? 2 : 0;
        result += this.state.filter.client ? 4 : 0;
        result += this.state.filter.judge ? 1 : 0;
        result += this.state.filter.staff ? 8 : 0;
        result += this.state.filter.attorney ? 16 : 0;
        result += this.state.filter.vendor ? 32 : 0;
        return result;
    }


    loadData = async () => {
        this.props.loading(true);
        
        let { page, size, order, direction, search, archived } = this.state;

        const __data = this.props.company
            ? await this.props.getCompanies({ page, size, order, direction, search })
            : await this.props.getPeople({ page, size, order, direction, search, flags: this.calculateFlags(), archived });
        let __count = 0;

        if (__data?.items?.length > 0)
            __count = Math.ceil(Number(__data.count) / Number(size));

        this.setState({
            ...this.state,
            data: __data.items,
            pages: __count
        });

        await this.props.pageInfo({
            title: this.props.company ? "Companies" : "People",
            breadcrumb: this.props.company ? "Companies" : "People"
        });
        this.props.loading(false);
    }

    get headers() {        
        const baseUrl = this.props.company ? "/dashboard/companies/" : "/dashboard/people/";
        return [
            {
                header: "Name",
                sortable: true,
                source: this.props.company ? "Person.displayName" : "lastName",
                className: "text-muted text-small text-uppercase w-35",
                onFormat: this.onNameLinkFormat
            },
            {
                header: "Email",
                sortable: true,
                source: "email",
                className: "text-muted text-small text-uppercase w-25",
                onFormat: (e, row) => onLinkFormat(e, row, { baseUrl, id: row["uuid"] })
            },
            {
                header: "Phone",
                sortable: true,
                source: "phone",
                className: "text-muted text-small text-uppercase w-25",
                onFormat: (e) => onPhoneLinkFormat(e)
            }
        ]
    }

    onNameLinkFormat = (e, row) => {
        const baseUrl = this.props.company ? "/dashboard/companies/" : "/dashboard/people/";
        if (this.props.company) {
            const text = row["displayName"] || row["Company.name"] || row["Company.dba"] || "";
            return onLinkFormat(e, row, { baseUrl, id: row["uuid"], text });
        }
        if (!(row["lastName"] && row["firstName"])) return "";
        return onLinkFormat(e, row, { baseUrl, id: row["uuid"], text: `${row["lastName"]}, ${row["firstName"]}` });
    }

    onNewClick = (e) => {
        this.props.navigate("add");
    }

    onImportClick = () => {
        if (this.importInput) {
            this.importInput.value = "";
            this.importInput.click();
        }
    }

    onImportFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        this.props.loading(true);
        try {
            const preview = await this.props.importPreview({ file });
            const resolutions = {};
            (preview?.conflicts || []).forEach((item) => {
                resolutions[item.id] = "current";
            });

            this.setState(prev => ({
                ...prev,
                import: {
                    show: true,
                    file,
                    preview,
                    resolutions,
                    importing: false,
                }
            }));
        } catch (error) {
            window.alert(error?.response?.data?.error?.message || error?.message || "Import preview failed.");
        } finally {
            this.props.loading(false);
        }
    }

    onImportResolutionChange = (id, choice) => {
        this.setState(prev => ({
            ...prev,
            import: {
                ...prev.import,
                resolutions: {
                    ...prev.import.resolutions,
                    [id]: choice,
                }
            }
        }));
    }

    onImportCancel = () => {
        this.setState(prev => ({
            ...prev,
            import: {
                show: false,
                file: null,
                preview: null,
                resolutions: {},
                importing: false,
            }
        }));
    }

    onImportApply = async () => {
        const { file, resolutions } = this.state.import;
        if (!file) return;

        this.setState(prev => ({
            ...prev,
            import: {
                ...prev.import,
                importing: true,
            }
        }));

        this.props.loading(true);
        try {
            const result = await this.props.importApply({ file, resolutions });
            const dataErrors = result?.applied?.errors?.length || 0;
            const fileErrors = result?.applied?.files?.errors?.length || 0;
            if (dataErrors || fileErrors) {
                window.alert(`Import completed with ${dataErrors + fileErrors} error(s).`);
            } else if (result?.applied?.files?.create || result?.applied?.files?.existing) {
                window.alert(`Import complete. ${result.applied.files.create} file(s) restored, ${result.applied.files.existing} already existed.`);
            }
            this.onImportCancel();
            await this.loadData();
        } catch (error) {
            window.alert(error?.response?.data?.error?.message || error?.message || "Import failed.");
            this.setState(prev => ({
                ...prev,
                import: {
                    ...prev.import,
                    importing: false,
                }
            }));
        } finally {
            this.props.loading(false);
        }
    }

    onFilterChange = (e) => {
        this.setState(prev => ({
            ...prev,
            filter: {
                ...prev.filter,
                [e.target.name]: e.target.checked
            }
        }), this.loadData);
    }

    onArchivedChange = (e) => {
        this.setState(prev => ({
            ...prev,
            archived: e.target.checked ? 1 : 0,
            page: 0
        }), this.loadData);
    }

    render() {
        return (
            <>
                <Col md="8" className="float-right text-end">
                    {!this.props.company ? (
                        <>
                            <input
                                ref={(el) => { this.importInput = el; }}
                                type="file"
                                accept=".zip,application/zip"
                                className="d-none"
                                onChange={this.onImportFileChange}
                            />
                            <ConditionalButton enabledVariant="outline-primary" tooltip="Import person extract" display enabled={true} onEnabledClick={this.onImportClick} icon="upload">Import</ConditionalButton>
                        </>
                    ) : null}
                    <ConditionalButton enabledVariant="outline-primary" tooltip={this.props.company ? "Create new company" : "Create new person"} display enabled={true} onEnabledClick={this.onNewClick} icon="flash">New</ConditionalButton>
                </Col>
                <Col xs="12">
                    <Table
                        headers={this.headers}
                        page={this.state.data}
                        sort={{ index: this.state.index, direction: this.state.direction }}
                        size={this.state.size}
                        index={this.state.page}
                        pages={this.state.pages}
                        onColumnClick={(e) => onColumnClick(e, this, this.loadData)}
                        onPageChange={(e) => this.setState({ page: Number(e)}, this.loadData)} 
                        onSearch={(e) => this.setState({ search: e}, this.loadData)}
                        onSize={(e) => this.setState({ size: Number(e), page: 0}, this.loadData)} 
                        searchOnEnter={true}
                        searchable={true}
                        sizable={false}
                        filter={this.props.company ? null : (
                            <Row className="mb-3">
                                <Col sm="12" md="6">
                                    <div className="d-inline-block">
                                        <Form.Label style={{marginLeft: "10px", marginTop: "6px", marginRight: "10px"}}>Leads</Form.Label>
                                        <Form.Check name="lead" className="form-check float-end mt-1" type="checkbox" checked={this.state.filter.lead} onChange={this.onFilterChange} />
                                    </div>
                                    <div className="d-inline-block">
                                        <Form.Label style={{marginLeft: "10px", marginTop: "6px", marginRight: "10px"}}>Clients</Form.Label>
                                        <Form.Check name="client" className="form-check float-end mt-1" type="checkbox" checked={this.state.filter.client} onChange={this.onFilterChange} />
                                    </div>
                                    <div className="d-inline-block">
                                        <Form.Label style={{marginLeft: "10px", marginTop: "6px", marginRight: "10px"}}>Attorney</Form.Label>
                                        <Form.Check name="attorney" className="form-check float-end mt-1" type="checkbox" checked={this.state.filter.attorney} onChange={this.onFilterChange} />
                                    </div>
                                    <div className="d-inline-block">
                                        <Form.Label style={{marginLeft: "10px", marginTop: "6px", marginRight: "10px"}}>Judge</Form.Label>
                                        <Form.Check name="judge" className="form-check float-end mt-1" type="checkbox" checked={this.state.filter.judge} onChange={this.onFilterChange} />
                                    </div>
                                    <div className="d-inline-block">
                                        <Form.Label style={{marginLeft: "10px", marginTop: "6px", marginRight: "10px"}}>Staff</Form.Label>
                                        <Form.Check name="staff" className="form-check float-end mt-1" type="checkbox" checked={this.state.filter.staff} onChange={this.onFilterChange} />
                                    </div>
                                    <div className="d-inline-block">
                                        <Form.Label style={{marginLeft: "10px", marginTop: "6px", marginRight: "10px"}}>Vendor</Form.Label>
                                        <Form.Check name="vendor" className="form-check float-end mt-1" type="checkbox" checked={this.state.filter.vendor} onChange={this.onFilterChange} />
                                    </div>
                                    <div className="d-inline-block">
                                        <Form.Label style={{marginLeft: "10px", marginTop: "6px", marginRight: "10px"}}>Archived</Form.Label>
                                        <Form.Check className="form-check float-end mt-1" type="checkbox" checked={this.state.archived === 1} onChange={this.onArchivedChange} />
                                    </div>
                                </Col>
                            </Row>
                        )}
                    />
                </Col>
                <PersonImportModal
                    show={this.state.import.show}
                    preview={this.state.import.preview}
                    resolutions={this.state.import.resolutions}
                    importing={this.state.import.importing}
                    onResolutionChange={this.onImportResolutionChange}
                    onCancel={this.onImportCancel}
                    onApply={this.onImportApply}
                />
            </>
        )
    }
}

People.propTypes = {
    auth: PropTypes.object.isRequired,
    profile: PropTypes.object
};

const mapStateToProps = (state) => {
    return ({
        auth: state.auth
    });
};

export default withNavigation(connect(mapStateToProps, { getPeople, getCompanies, importPreview, importApply, loading, pageInfo })(People));
